import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  removeMember,
  updateMemberRole,
  leaveArtistGroup,
  deleteArtistGroup,
  cancelInvite,
} from "../../../../services/api";
import { useUserRole } from "../../../../context/UserRoleContext";
import { useArtist } from "../../../../context/ArtistContext";
import AddMembersModal from "./AddMembersModal";
import SelectOwnerModal from "./SelectOwnerModal";
import EditArtistModal from "./EditArtistModal";
import ConfirmModal from "../../../../components/common/ConfirmModal";
import GroupMembersDropdown from "../../../../components/messaging/GroupMembersDropdown";
import artistGif from "../../../../assets/images/artist.gif";
import { artistGenresList } from "../../../../utils/genres";

const ArtistProfileModal = ({
  isOpen,
  onClose,
  artist,
  onArtistUpdate,
  onArtistDeleted,
  onMembersChanged,
  isMapsLoaded = false,
}) => {
  const { userData } = useUserRole();
  const {
    getGroupModalMembers,
    fetchGroupModalMembers,
    refreshGroupModalMembers,
    invalidateGroupModalMembers,
    refreshArtists,
  } = useArtist();

  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showSelectOwnerModal, setShowSelectOwnerModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMessageDropdown, setShowMessageDropdown] = useState(false);
  const messageButtonRef = useRef(null);

  const [confirmRemove, setConfirmRemove] = useState({
    open: false,
    userId: null,
    memberName: null,
  });
  const [confirmPromote, setConfirmPromote] = useState({
    open: false,
    userId: null,
    memberName: null,
  });
  const [confirmLeave, setConfirmLeave] = useState({
    open: false,
    message: "",
    title: "",
  });
  const [confirmLeaveWithTransfer, setConfirmLeaveWithTransfer] = useState({
    open: false,
  });
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(false);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);
  const [confirmRemovePending, setConfirmRemovePending] = useState({
    open: false,
    inviteId: null,
    inviteEmail: null,
  });

  const currentArtist = artist;
  const artistId = currentArtist?.artist_id;
  const genreList = currentArtist ? artistGenresList(currentArtist) : [];

  const { members, loading, error } = getGroupModalMembers(artistId);

  const isOwner = members.some(
    (m) => m.user_id === userData?.user_id && m.role === "owner",
  );
  const currentUserMember = members.find(
    (m) => m.user_id === userData?.user_id && m.status !== "pending",
  );
  const otherMembers = members.filter(
    (m) => m.user_id !== userData?.user_id && m.status !== "pending",
  );

  useEffect(() => {
    if (isOpen && artistId) {
      fetchGroupModalMembers(artistId);
    }
  }, [isOpen, artistId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!error || !isOpen || !artistId) return;
    onClose();
    invalidateGroupModalMembers(artistId);
    alert(error);
  }, [error, isOpen, artistId, onClose, invalidateGroupModalMembers]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleRemoveMember = (userId) => {
    const member = members.find((m) => m.user_id === userId);
    const memberName = member?.name || member?.email || "this member";
    setConfirmRemove({ open: true, userId, memberName });
  };

  const handleRemoveMemberConfirm = async () => {
    const { userId } = confirmRemove;
    setConfirmRemove({ open: false, userId: null, memberName: null });

    try {
      await removeMember(currentArtist.artist_id, userId);
      await refreshGroupModalMembers(currentArtist.artist_id);
      onMembersChanged?.();
      refreshArtists();
    } catch (err) {
      console.error("Error removing member:", err);
      alert(err.message || "Failed to remove member");
    }
  };

  const handlePromoteMember = (userId) => {
    const member = members.find((m) => m.user_id === userId);
    const memberName = member?.name || member?.email || "this member";
    setConfirmPromote({ open: true, userId, memberName });
  };

  const handlePromoteMemberConfirm = async () => {
    const { userId } = confirmPromote;
    setConfirmPromote({ open: false, userId: null, memberName: null });

    try {
      await updateMemberRole(currentArtist.artist_id, userId, "owner");
      const updatedMembers = await refreshGroupModalMembers(
        currentArtist.artist_id,
      );
      onMembersChanged?.();
      await refreshArtists();

      const currentUserStillOwner = Array.isArray(updatedMembers)
        ? updatedMembers.some(
            (m) => m.user_id === userData?.user_id && m.role === "owner",
          )
        : false;
      if (!currentUserStillOwner) {
        onClose();
      }
    } catch (err) {
      console.error("Error promoting member:", err);
      alert(err.message || "Failed to promote member");
    }
  };

  const handleRemovePendingInvite = (inviteId, inviteEmail) => {
    setConfirmRemovePending({ open: true, inviteId, inviteEmail });
  };

  const handleRemovePendingInviteConfirm = async () => {
    const { inviteId } = confirmRemovePending;
    setConfirmRemovePending({ open: false, inviteId: null, inviteEmail: null });

    try {
      await cancelInvite(inviteId);
      await refreshGroupModalMembers(currentArtist.artist_id);
      onMembersChanged?.();
      refreshArtists();
    } catch (err) {
      console.error("Error removing pending invite:", err);
      alert(err.message || "Failed to remove pending invite");
    }
  };

  const handleInviteSent = useCallback(async () => {
    await refreshGroupModalMembers(currentArtist.artist_id);
    onMembersChanged?.();
    refreshArtists();
  }, [
    currentArtist?.artist_id,
    refreshGroupModalMembers,
    onMembersChanged,
    refreshArtists,
  ]);

  const handleLeaveGroup = () => {
    if (currentUserMember?.role === "owner" && otherMembers.length > 0) {
      setShowSelectOwnerModal(true);
      return;
    }
    if (currentUserMember?.role === "owner" && otherMembers.length === 0) {
      setConfirmLeave({
        open: true,
        title: "Delete group?",
        message:
          "You are the last member. This will delete the artist group. Future shows will be cancelled and bars will be notified. Past shows and earnings for bars will remain. This cannot be undone.",
      });
      return;
    }
    setConfirmLeave({
      open: true,
      title: "Leave Artist Group?",
      message: "Are you sure you want to leave this artist group?",
    });
  };

  const handleDeleteGroupClick = () => {
    setConfirmDeleteGroup(true);
  };

  const handleDeleteGroupConfirm = async () => {
    if (!currentArtist?.artist_id || isDeletingGroup) return;
    try {
      setIsDeletingGroup(true);
      const result = await deleteArtistGroup(currentArtist.artist_id);
      if (result?.success) {
        onArtistDeleted?.(currentArtist.artist_id);
        refreshArtists();
        setConfirmDeleteGroup(false);
        onClose();
      } else {
        alert(result?.error || "Failed to delete group");
      }
    } catch (err) {
      alert(err.message || "Failed to delete group");
    } finally {
      setIsDeletingGroup(false);
    }
  };

  const handleSelectOwnerConfirm = (newOwnerId) => {
    setShowSelectOwnerModal(false);
    setConfirmLeaveWithTransfer({ open: true, newOwnerId });
  };

  const handleLeaveGroupConfirm = async (newOwnerId) => {
    setConfirmLeave({ open: false, message: "", title: "" });
    setConfirmLeaveWithTransfer({ open: false });

    try {
      const response = await leaveArtistGroup(
        currentArtist.artist_id,
        newOwnerId,
      );
      if (response.success) {
        if (response.artistDeleted) {
          onArtistDeleted?.(currentArtist.artist_id);
          refreshArtists();
          alert("You have left the artist group. The artist has been deleted.");
        } else {
          await refreshGroupModalMembers(currentArtist.artist_id);
          onMembersChanged?.();
          refreshArtists();
          alert("You have successfully left the artist group.");
        }
        onClose();
      }
    } catch (err) {
      console.error("Error leaving artist group:", err);
      alert(err.message || "Failed to leave artist group");
    }
  };

  if (!isOpen || !currentArtist) {
    return null;
  }

  if (error) {
    return null;
  }

  const activeMembers = members.filter((m) => m.status !== "pending");
  const memberCount = activeMembers.length;

  return (
    <div
      className="artist-profile-modal-overlay"
      role="presentation"
      onClick={handleOverlayClick}
    >
      <div
        className="artist-profile-modal groups-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="artist-profile-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="artist-profile-modal-header">
          <h2 id="artist-profile-modal-title">
            {currentArtist.stage_name || "Group"}
          </h2>
          <div className="artist-profile-modal-header-actions">
            {!loading && isOwner && (
              <>
                <button
                  type="button"
                  className="artist-profile-invite-btn"
                  onClick={() => setShowAddMembersModal(true)}
                >
                  Add Members
                </button>
                <button
                  type="button"
                  className="artist-profile-invite-btn"
                  onClick={() => setShowEditModal(true)}
                >
                  Edit
                </button>
              </>
            )}
            {!loading && memberCount > 1 && (
              <button
                ref={messageButtonRef}
                type="button"
                className="artist-profile-message-btn"
                onClick={() => setShowMessageDropdown(true)}
                aria-label="Message group members"
              >
                💬
              </button>
            )}
            <button
              type="button"
              className="artist-profile-modal-close"
              aria-label="Close artist profile"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        {showMessageDropdown && (
          <GroupMembersDropdown
            isOpen={showMessageDropdown}
            onClose={() => setShowMessageDropdown(false)}
            members={otherMembers}
            buttonRef={messageButtonRef}
          />
        )}

        <div className="artist-profile-modal-body groups-modal-body">
          {loading ? (
            <div className="groups-modal-loading groups-modal-loading--centered">
              <span className="spinner" aria-hidden="true" />
              <span>Loading…</span>
            </div>
          ) : (
            <>
              <div className="onboarding-section">
                <h2 className="section-title">Artist Information</h2>
                <div className="groups-modal-info-section">
                  <div className="groups-modal-photo-wrapper">
                    <img
                      src={currentArtist.artist_pic || artistGif}
                      alt={currentArtist.stage_name || "Group"}
                      className="groups-modal-photo"
                      onError={(e) => {
                        e.currentTarget.src = artistGif;
                      }}
                    />
                  </div>
                  <div className="groups-modal-info-fields">
                    <div className="form-grid">
                      {currentArtist.type && (
                        <div className="input-group">
                          <span className="input-label">Type</span>
                          <div className="display-value">
                            {currentArtist.type}
                          </div>
                        </div>
                      )}
                      <div className="input-group">
                        <span className="input-label">Member Count</span>
                        <div className="display-value">{memberCount}</div>
                      </div>
                      {genreList.length > 0 && (
                        <div className="input-group">
                          <span className="input-label">Genres</span>
                          <div className="display-value">
                            <div className="artist-genre-pills">
                              {genreList.map((g) => (
                                <span key={g} className="artist-genre-pill">
                                  {g}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      {currentArtist.hometown && (
                        <div className="input-group">
                          <span className="input-label">Hometown</span>
                          <div className="display-value">
                            {currentArtist.hometown}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="onboarding-section">
                <h2 className="section-title">Description</h2>
                {currentArtist.description && (
                  <p className="groups-modal-description">
                    {currentArtist.description}
                  </p>
                )}
              </div>

              <div className="onboarding-section">
                <h2 className="section-title">Members</h2>
                {members.length > 0 ? (
                  <div className="groups-modal-members">
                    {members.map((member) => (
                      <div
                        key={member.user_id || member.invite_id}
                        className="groups-modal-member-tile"
                      >
                        <div className="groups-modal-member-info">
                          <span className="groups-modal-member-name">
                            {member.name || member.email}
                          </span>
                          {member.status === "pending" && (
                            <span className="groups-modal-member-badge pending">
                              Pending
                            </span>
                          )}
                          {member.role === "owner" && (
                            <span className="groups-modal-member-role">
                              Owner
                            </span>
                          )}
                          {member.role === "member" &&
                            member.status !== "pending" && (
                              <span className="groups-modal-member-role">
                                Member
                              </span>
                            )}
                        </div>
                        {member.user_id === userData?.user_id &&
                          member.status !== "pending" && (
                            <div className="groups-modal-member-actions">
                              {isOwner && otherMembers.length > 0 && (
                                <button
                                  type="button"
                                  className="groups-modal-action-btn remove"
                                  onClick={handleDeleteGroupClick}
                                  title="Delete group"
                                >
                                  Delete group
                                </button>
                              )}
                              <button
                                type="button"
                                className="groups-modal-action-btn remove"
                                onClick={handleLeaveGroup}
                                title={isOwner && otherMembers.length === 0 ? "Delete group" : "Leave Group"}
                              >
                                {isOwner && otherMembers.length === 0
                                  ? "Delete group"
                                  : "Leave Group"}
                              </button>
                            </div>
                          )}
                        {isOwner && member.status === "pending" && (
                          <div className="groups-modal-member-actions">
                            <button
                              type="button"
                              className="groups-modal-action-btn remove"
                              onClick={() =>
                                handleRemovePendingInvite(
                                  member.invite_id,
                                  member.email,
                                )
                              }
                              title="Remove Pending Invite"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                        {isOwner &&
                          member.status !== "pending" &&
                          member.user_id !== userData?.user_id && (
                            <div className="groups-modal-member-actions">
                              {member.role === "member" && (
                                <button
                                  type="button"
                                  className="groups-modal-action-btn"
                                  onClick={() =>
                                    handlePromoteMember(member.user_id)
                                  }
                                  title="Promote to Owner"
                                >
                                  Promote
                                </button>
                              )}
                              <button
                                type="button"
                                className="groups-modal-action-btn remove"
                                onClick={() =>
                                  handleRemoveMember(member.user_id)
                                }
                                title="Remove Member"
                              >
                                Remove
                              </button>
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="groups-modal-empty">No members found</p>
                )}
              </div>

              <div className="onboarding-section">
                <h2 className="section-title">Socials</h2>
                <div className="form-grid">
                  {currentArtist.facebook && (
                    <div className="input-group">
                      <span className="input-label">Facebook</span>
                      <div className="display-value">
                        <a
                          href={
                            currentArtist.facebook.startsWith("http")
                              ? currentArtist.facebook
                              : `https://facebook.com/${currentArtist.facebook}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="groups-modal-social-link"
                        >
                          {currentArtist.facebook}
                        </a>
                      </div>
                    </div>
                  )}
                  {currentArtist.instagram && (
                    <div className="input-group">
                      <span className="input-label">Instagram</span>
                      <div className="display-value">
                        <a
                          href={
                            currentArtist.instagram.startsWith("http")
                              ? currentArtist.instagram
                              : `https://instagram.com/${currentArtist.instagram}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="groups-modal-social-link"
                        >
                          {currentArtist.instagram}
                        </a>
                      </div>
                    </div>
                  )}
                  {currentArtist.website && (
                    <div className="input-group">
                      <span className="input-label">Website</span>
                      <div className="display-value">
                        <a
                          href={
                            currentArtist.website.startsWith("http")
                              ? currentArtist.website
                              : `https://${currentArtist.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="groups-modal-social-link"
                        >
                          {currentArtist.website}
                        </a>
                      </div>
                    </div>
                  )}
                  {currentArtist.venmo && (
                    <div className="input-group">
                      <span className="input-label">Venmo</span>
                      <div className="display-value">{currentArtist.venmo}</div>
                    </div>
                  )}
                </div>
              </div>

              {(currentArtist.latitude != null ||
                currentArtist.longitude != null) && (
                <div className="onboarding-section">
                  <h2 className="section-title">Coordinates</h2>
                  <div className="form-grid">
                    <div className="input-group">
                      <span className="input-label">Latitude</span>
                      <div className="display-value">
                        {currentArtist.latitude != null
                          ? currentArtist.latitude
                          : "N/A"}
                      </div>
                    </div>
                    <div className="input-group">
                      <span className="input-label">Longitude</span>
                      <div className="display-value">
                        {currentArtist.longitude != null
                          ? currentArtist.longitude
                          : "N/A"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {!loading && (
        <>
          <AddMembersModal
            isOpen={showAddMembersModal}
            onClose={() => setShowAddMembersModal(false)}
            artistId={currentArtist.artist_id}
            onInviteSent={handleInviteSent}
            members={members}
            currentUserId={userData?.user_id}
          />

          <EditArtistModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            artist={currentArtist}
            isMapsLoaded={isMapsLoaded}
            onSuccess={(updatedArtist) => {
              onArtistUpdate?.(updatedArtist);
              setShowEditModal(false);
            }}
          />

          <SelectOwnerModal
            isOpen={showSelectOwnerModal}
            onClose={() => setShowSelectOwnerModal(false)}
            members={otherMembers}
            onConfirm={handleSelectOwnerConfirm}
          />
        </>
      )}

      <ConfirmModal
        open={confirmRemove.open}
        title="Remove Member?"
        message={`Are you sure you want to remove ${confirmRemove.memberName} from this artist group? This action cannot be undone.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleRemoveMemberConfirm}
        onCancel={() =>
          setConfirmRemove({ open: false, userId: null, memberName: null })
        }
      />

      <ConfirmModal
        open={confirmRemovePending.open}
        title="Remove Pending Invite?"
        message={`Are you sure you want to remove the pending invite for ${confirmRemovePending.inviteEmail}? The recipient will no longer see the invitation.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleRemovePendingInviteConfirm}
        onCancel={() =>
          setConfirmRemovePending({
            open: false,
            inviteId: null,
            inviteEmail: null,
          })
        }
      />

      <ConfirmModal
        open={confirmPromote.open}
        title="Promote to Owner?"
        message={`Are you sure you want to promote ${confirmPromote.memberName} to owner? They will have full control over this artist group.`}
        confirmLabel="Promote"
        cancelLabel="Cancel"
        onConfirm={handlePromoteMemberConfirm}
        onCancel={() =>
          setConfirmPromote({ open: false, userId: null, memberName: null })
        }
      />

      <ConfirmModal
        open={confirmLeave.open}
        title={confirmLeave.title}
        message={confirmLeave.message}
        confirmLabel="Leave"
        cancelLabel="Cancel"
        onConfirm={() => handleLeaveGroupConfirm(null)}
        onCancel={() =>
          setConfirmLeave({ open: false, message: "", title: "" })
        }
      />

      <ConfirmModal
        open={confirmLeaveWithTransfer.open}
        title="Transfer Ownership and Leave?"
        message="Are you sure you want to leave this artist group and transfer ownership?"
        confirmLabel="Leave"
        cancelLabel="Cancel"
        onConfirm={() =>
          handleLeaveGroupConfirm(confirmLeaveWithTransfer.newOwnerId)
        }
        onCancel={() => setConfirmLeaveWithTransfer({ open: false })}
      />

      <ConfirmModal
        open={confirmDeleteGroup}
        title="Delete group?"
        message="This will dissolve the artist group. Future shows will be cancelled and bars will be notified. Other members will be notified. Past shows and earnings for bars will remain. This cannot be undone."
        confirmLabel="Delete group"
        cancelLabel="Cancel"
        isConfirming={isDeletingGroup}
        onConfirm={handleDeleteGroupConfirm}
        onCancel={() => {
          if (!isDeletingGroup) setConfirmDeleteGroup(false);
        }}
      />
    </div>
  );
};

export default ArtistProfileModal;
